import { useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import {
  PRESIGNED_URL_REFETCH_INTERVAL,
  PRESIGNED_URL_STALE_TIME,
} from '@/hooks/opportunity/presignedFileUrls'
import {
  buildOpportunityDetailQueryString,
  type OpportunityDetailFetchOptions,
} from '@/hooks/opportunity/queryParams'

import type { Sbir } from '@/types/sbir/Sbir'
import type { SearchOut } from '@/types/Search'

export const fetchSbir = async (
  sbirId: string,
  options?: OpportunityDetailFetchOptions,
): Promise<Sbir | null> => {
  if (sbirId === '') {
    return null
  }
  const params = buildOpportunityDetailQueryString(options)
  const url = `${API_URL}/api/v1/sbirs/${sbirId}${params}`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) {
    throw res
  }
  return await res.json()
}

export const sbirWithoutPresignedUrlsQueryKey = (sbirId: string) =>
  [sbirId, 'sbir', 'withoutPresignedUrls'] as const

export const fetchSbirWithoutPresignedUrls = (sbirId: string) =>
  fetchSbir(sbirId, { includePresignedUrls: false })

export const useSuspenseSbirWithoutPresignedUrls = (sbirId: string) => {
  return useSuspenseQuery({
    queryKey: sbirWithoutPresignedUrlsQueryKey(sbirId),
    queryFn: async () => {
      const data = await fetchSbirWithoutPresignedUrls(sbirId)
      if (!data) throw new Response(null, { status: 404 })
      return data
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

export const sbirPresignedUrlsQueryKey = (sbirId: string) =>
  [sbirId, 'sbir', 'presignedUrls'] as const

// Live client-side fetch with presigned file URLs; the page shows it in place of the ISR copy.
export const useSbirPresignedUrls = (sbirId: string) => {
  return useQuery({
    queryKey: sbirPresignedUrlsQueryKey(sbirId),
    queryFn: () => fetchSbir(sbirId),
    staleTime: PRESIGNED_URL_STALE_TIME,
    refetchInterval: PRESIGNED_URL_REFETCH_INTERVAL,
    enabled: sbirId !== '',
  })
}

export const useSbir = (sbirId: string, initialData?: Sbir) => {
  return useQuery({
    queryKey: [sbirId, 'sbir'],
    queryFn: () => fetchSbir(sbirId),
    initialData: initialData,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: sbirId !== '',
  })
}

export const fetchSimilarSbirs = async (
  sbirId: string,
): Promise<Array<SearchOut>> => {
  if (sbirId === '') {
    return []
  }
  const url = `${API_URL}/api/v1/sbirs/${sbirId}/similar`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) {
    throw res
  }
  return await res.json()
}

export const useSimilarSbirs = (sbirId: string) => {
  return useQuery({
    queryKey: [sbirId, 'similarSbirs'],
    queryFn: () => fetchSimilarSbirs(sbirId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: sbirId !== '',
  })
}
